import React, { useState, useEffect, useRef } from 'react';
import { FlatList } from 'react-native';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  Alert,
  Platform,
  Keyboard,
  Dimensions,
  ActivityIndicator,
  Linking,
  Image,
} from 'react-native';
import { InsetModal as Modal } from './InsetModal';
import { colors, spacing, typography, chat } from '../theme';
import { designTokens } from '../theme/design-system';
import { Ionicons } from '@expo/vector-icons';
import { DirectionalIcon } from './DirectionalIcon';
import { VoiceRecorder } from './VoiceRecorder';
import { MapLocationPicker } from './MapLocationPicker';
import { RichTextEditor } from './design-system/RichTextEditor';
import { MediaService } from '../services/media.service';
import { getPreviewData } from '@flyerhq/react-native-link-preview';
import { apiService } from '../services/api';
import { useAppContext } from '../contexts/AppContext';
import { useLocale } from '../contexts/LocaleContext';
import { createTranslator } from '../i18n';
import { useTypography } from '../hooks/useTypography';
import { useRTLStyles } from '../hooks/useRTLStyles';
import EmojiPicker, { EmojiKeyboard } from 'rn-emoji-keyboard';
import * as FileSystem from 'expo-file-system/legacy';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { withAlpha } from '../theme';

const { width: screenWidth } = Dimensions.get('window');

const STICKERS = [
  {
    id: 'party-parrot',
    name: 'Party Parrot',
    tags: ['party', 'celebrate', 'fun'],
    url: 'https://media.giphy.com/media/26ufdipQqU2lhNA4g/giphy.gif',
  },
  {
    id: 'thumbs-up',
    name: 'Thumbs Up',
    tags: ['ok', 'yes', 'approve'],
    url: 'https://media.giphy.com/media/111ebonMs90YLu/giphy.gif',
  },
  {
    id: 'love',
    name: 'Love',
    tags: ['love', 'heart', 'like'],
    url: 'https://media.giphy.com/media/3oEjI6SIIHBdRxXI40/giphy.gif',
  },
  {
    id: 'wow',
    name: 'Wow',
    tags: ['wow', 'surprise'],
    url: 'https://media.giphy.com/media/l0HlBO7eyXzSZkJri/giphy.gif',
  },
  {
    id: 'clap',
    name: 'Clap',
    tags: ['clap', 'bravo'],
    url: 'https://media.giphy.com/media/5GoVLqeAOo6PK/giphy.gif',
  },
  {
    id: 'rocket',
    name: 'Rocket',
    tags: ['rocket', 'launch', 'go'],
    url: 'https://media.giphy.com/media/3ohs7KViF6rA4aan5u/giphy.gif',
  },
];

interface MessageInputProps {
  onSendMessage: (
    content: string,
    type?: 'text' | 'image' | 'video' | 'audio' | 'document' | 'location' | 'location_request' | 'interactive',
    mediaUri?: string,
    mediaDuration?: number,
    locationData?: {
      latitude: number;
      longitude: number;
      address: string;
    },
    mediaMetadata?: {
      fileName?: string;
      fileSize?: number;
      mimeType?: string;
    },
    messageOptions?: {
      referenceType?: 'product' | 'catalog' | 'calendar' | 'order';
      referenceId?: string;
      interactiveType?: string;
      interactive?: any;
      selectedCatalog?: {
        _id: string;
        name: string;
      };
    }
  ) => void;
  sending?: boolean;
  placeholder?: string;
  disabled?: boolean;
  sendButtonDisabled?: boolean;
  singleLine?: boolean;
  showAttachment?: boolean;
  showSendButton?: boolean;
  value?: string;
  onChangeText?: (text: string) => void;
  horizontalInset?: number;
  conversationId?: string; // For sending quick reply sets
  /** Opens "send a quotation / an order" (shown only when given). */
  onShareDocument?: (type: 'quotation' | 'order') => void;
  /** Adds options (buttons / list) to the message being written. */
  onAddOptions?: () => void;
  /** Rendered inside a host screen's own pane (ConversationScreen's embedded
   * mode) instead of full-screen — sizes the attachment/media-options modal
   * to a fixed pane-appropriate width instead of the full browser window,
   * since `horizontalInset` alone (meant for a full-width screen minus its
   * side panes) would otherwise still stretch the modal to screenWidth. */
  embedded?: boolean;
}

export const MessageInputWithVoice: React.FC<MessageInputProps> = ({
  onSendMessage,
  sending = false,
  placeholder,
  disabled = false,
  sendButtonDisabled = false,
  singleLine = false,
  showAttachment = true,
  showSendButton = true,
  value,
  onChangeText,
  horizontalInset = 0,
  conversationId,
  onShareDocument,
  onAddOptions,
  embedded = false,
}) => {
  const styles = useThemedStyles(getStyles);
  const [internalMessage, setInternalMessage] = useState('');
  const message = value !== undefined ? value : internalMessage;
  const setMessage = (text: string) => {
    if (value === undefined) {
      setInternalMessage(text);
    }
    onChangeText?.(text);
  };
  const [showMediaOptions, setShowMediaOptions] = useState(false);
  const [showQuickReplySelector, setShowQuickReplySelector] = useState(false);
  const [quickReplySets, setQuickReplySets] = useState<Array<{ _id: string; name: string; description?: string; itemCount?: number }>>([]);
  const [loadingQuickReplySets, setLoadingQuickReplySets] = useState(false);
  const [sendingQuickReplySet, setSendingQuickReplySet] = useState(false);
  const [quickReplySearchQuery, setQuickReplySearchQuery] = useState('');
  const [showCatalogSelector, setShowCatalogSelector] = useState(false);
  const [catalogues, setCatalogues] = useState<Array<{ _id: string; name: string; description?: string; productCount?: number }>>([]);
  const [loadingCatalogues, setLoadingCatalogues] = useState(false);
  const [catalogSearchQuery, setCatalogSearchQuery] = useState('');
  const [selectedCatalog, setSelectedCatalog] = useState<{ _id: string; name: string } | null>(null);
  const [showEmojiKeyboard, setShowEmojiKeyboard] = useState(false);
  const [showStickerPicker, setShowStickerPicker] = useState(false);
  const [stickerSearchQuery, setStickerSearchQuery] = useState('');
  const [isStickerLoading, setIsStickerLoading] = useState(false);
  const { showNotification } = useAppContext();
  const { isRTL, locale } = useLocale();
  const t = createTranslator(locale);
  const typo = useTypography();
  const rtl = useRTLStyles();

  // Load quick reply sets when modal opens
  useEffect(() => {
    if (showQuickReplySelector) {
      loadQuickReplySets();
    }
  }, [showQuickReplySelector]);

  // Load catalogues when modal opens
  useEffect(() => {
    if (showCatalogSelector) {
      loadCatalogues();
    }
  }, [showCatalogSelector]);

  const loadQuickReplySets = async () => {
    try {
      setLoadingQuickReplySets(true);
      const response = await apiService.getQuickReplySets({
        isActive: true,
        limit: 100,
        skip: 0,
      });
      if (response.statusCode === 1000 && response.data?.quickReplySets) {
        setQuickReplySets(response.data.quickReplySets);
      }
    } catch (error) {
      console.error('Error loading quick reply sets:', error);
      showNotification('error', t('common.error'), t('chat.errorLoadingQuickReplies'));
    } finally {
      setLoadingQuickReplySets(false);
    }
  };

  const handleSendQuickReplySet = async (setId: string) => {
    if (!conversationId) {
      showNotification('error', t('common.error'), t('chat.conversationIdRequired'));
      return;
    }

    try {
      setSendingQuickReplySet(true);
      setShowQuickReplySelector(false);
      
      const response = await apiService.sendQuickReplySet(setId, conversationId);
      
      if (response.statusCode === 1000) {
        showNotification('success', t('common.success'), t('chat.quickReplySent'));
        // The message will appear via socket updates
      } else {
        showNotification('error', t('common.error'), response.errors?.[0]?.message || t('chat.quickReplySendFailed'));
      }
    } catch (error: any) {
      console.error('Error sending quick reply set:', error);
      showNotification('error', t('common.error'), error.message || t('chat.quickReplySendFailed'));
    } finally {
      setSendingQuickReplySet(false);
    }
  };

  const filteredQuickReplySets = quickReplySets.filter((set) =>
    set.name.toLowerCase().includes(quickReplySearchQuery.toLowerCase())
  );

  const loadCatalogues = async () => {
    try {
      setLoadingCatalogues(true);
      const response = await apiService.getCatalogues({
        isActive: true,
        limit: 100,
        skip: 0,
      });
      if (response.statusCode === 1000 && response.data?.catalogues) {
        setCatalogues(response.data.catalogues);
      }
    } catch (error) {
      console.error('Error loading catalogues:', error);
      showNotification('error', t('common.error'), t('chat.errorLoadingCatalogues'));
    } finally {
      setLoadingCatalogues(false);
    }
  };

  const handleSelectCatalog = (catalog: { _id: string; name: string }) => {
    setSelectedCatalog({ _id: catalog._id, name: catalog.name });
    setShowCatalogSelector(false);
    setCatalogSearchQuery('');
  };

  const filteredCatalogues = catalogues.filter((catalog) =>
    catalog.name.toLowerCase().includes(catalogSearchQuery.toLowerCase())
  );
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [showLocationPicker, setShowLocationPicker] = useState(false);
  const [isMediaLoading, setIsMediaLoading] = useState(false);
  const isWeb = Platform.OS === 'web';
  const isMobileBrowser = isWeb && screenWidth < 768;
  const modalHorizontalInset = isWeb && !isMobileBrowser ? Math.max(horizontalInset, 0) : 0;
  // Embedded: screenWidth is the full browser window, not the (much
  // narrower) pane this component actually renders inside — sizing the
  // modal off it would stretch it to the full window regardless of
  // horizontalInset. Use a fixed, pane-appropriate width instead.
  const modalContentWidth = embedded ? 320 : Math.max(screenWidth - modalHorizontalInset * 2, 320);
  const mediaGridAvailableWidth = modalContentWidth - designTokens.spacing.lg * 2;
  const mediaOptionWidth = Math.max(
    (mediaGridAvailableWidth - designTokens.spacing.md * 2) / 3,
    96
  );

  const ensurePermission = async (
    permission: 'camera' | 'mediaLibrary' | 'location' | 'microphone' | 'contacts',
    label: string
  ) => {
    if (Platform.OS === 'web') return true;
    const granted = await MediaService.requestPermission(permission);
    if (!granted) {
      Alert.alert(
        'Permission Required',
        `Please grant ${label} permission to continue.`,
        [
          { text: 'Cancel', style: 'cancel' },
          {
            text: 'Open Settings',
            onPress: () => Linking.openSettings(),
          },
        ]
      );
    }
    return granted;
  };
  
  const handleSend = () => {
    try {
      console.log('📤 [MessageInputWithVoice] handleSend called');
      const trimmedMessage = message.trim();
      console.log('📤 [MessageInputWithVoice] Message length:', trimmedMessage.length);
      
      if (!trimmedMessage || sending || disabled) {
        if (!selectedCatalog || sending || disabled) {
          console.log('📤 [MessageInputWithVoice] Send blocked - empty:', !trimmedMessage, 'sending:', sending, 'disabled:', disabled, 'hasCatalog:', !!selectedCatalog);
          return;
        }
      }

      const messageOptions = selectedCatalog
        ? {
            referenceType: 'catalog' as const,
            referenceId: selectedCatalog._id,
            interactiveType: 'catalog',
            interactive: {
              type: 'catalog',
              selectedCatalog,
            },
            selectedCatalog,
          }
        : undefined;

      console.log('📤 [MessageInputWithVoice] Calling onSendMessage with options:', messageOptions);
      onSendMessage(
        trimmedMessage,
        selectedCatalog ? 'interactive' : 'text',
        undefined,
        undefined,
        undefined,
        undefined,
        messageOptions
      );

      if (selectedCatalog) {
        setSelectedCatalog(null);
      }

      // Only clear message if not controlled (no value prop provided)
      if (value === undefined) {
        setMessage('');
        // Keep keyboard open
      }

      console.log('📤 [MessageInputWithVoice] Send completed successfully');
    } catch (error) {
      console.error('❌ [MessageInputWithVoice] Error in handleSend:', error);
    }
  };

  const clearSelectedCatalog = () => {
    setSelectedCatalog(null);
  };

  const handleVoiceRecordingComplete = (audioUri: string, duration: number) => {
    console.log('🎤 Voice recording completed:', { audioUri, duration });
    
    // Send the voice message - use empty content for proper voice note
    onSendMessage('', 'audio', audioUri, duration);
    setShowVoiceRecorder(false);
  };

  const handleVoiceRecordingCancel = () => {
    console.log('🎤 Voice recording cancelled');
    setShowVoiceRecorder(false);
  };

  const handleLocationSelect = (location: {
    latitude: number;
    longitude: number;
    address: string;
  }) => {
    console.log('📍 Location selected:', location);
    onSendMessage(
      message.trim(),
      'location',
      undefined, // mediaUri
      undefined, // mediaDuration
      location   // locationData
    );
    setMessage('');
    setShowLocationPicker(false);
  };

  const handleLocationPickerClose = () => {
    setShowLocationPicker(false);
  };

  const mediaOptions = [
    {
      id: 'camera',
      title: 'Camera',
      icon: 'camera',
      color: '#4CAF50',
      onPress: () => handleMediaOption('camera')
    },
    {
      id: 'gallery',
      title: 'Gallery',
      icon: 'images',
      color: '#2196F3',
      onPress: () => handleMediaOption('gallery')
    },
    {
      id: 'video',
      title: 'Video',
      icon: 'videocam',
      color: '#FF5722',
      onPress: () => handleMediaOption('video')
    },
    {
      id: 'audio',
      title: 'Voice Note',
      icon: 'mic',
      color: '#9C27B0',
      onPress: () => handleMediaOption('audio')
    },
    {
      id: 'document',
      title: 'Document',
      icon: 'document-text',
      color: '#FF9800',
      onPress: () => handleMediaOption('document')
    },
    {
      id: 'location',
      title: 'Location',
      icon: 'location',
      color: '#F44336',
      onPress: () => handleMediaOption('location')
    },
    {
      id: 'location_request',
      title: 'Request Location',
      icon: 'location-outline',
      color: '#FF9800',
      onPress: () => handleMediaOption('location_request')
    },
    {
      id: 'quick_replies',
      title: 'Quick Replies',
      icon: 'chatbubble-ellipses-outline',
      color: '#F59E0B',
      onPress: () => {
        if (conversationId) {
          setShowQuickReplySelector(true);
        } else {
          showNotification('error', 'Error', 'Conversation ID is required to send quick replies');
        }
      }
    },
    ...(onAddOptions ? [
      { id: 'options', title: t('ai.choices.menu'), icon: 'list-outline', color: '#0EA5E9', onPress: () => openAfterMenuCloses(onAddOptions) },
    ] : []),
    ...(onShareDocument ? [
      { id: 'send_quotation', title: t('share.sendQuotation'), icon: 'document-text-outline', color: '#6366F1', onPress: () => openAfterMenuCloses(() => onShareDocument('quotation')) },
      { id: 'send_order', title: t('share.sendOrder'), icon: 'receipt-outline', color: '#10B981', onPress: () => openAfterMenuCloses(() => onShareDocument('order')) },
    ] : []),
    {
      id: 'catalog',
      title: 'Catalog',
      icon: 'grid-outline',
      color: '#8B5CF6',
      onPress: () => {
        if (conversationId) {
          setShowCatalogSelector(true);
        } else {
          showNotification('error', 'Error', 'Conversation ID is required to send catalog');
        }
      }
    },
  ];

  // iOS can't present a modal (or the native camera/photo picker) while
  // another modal is still on screen or animating out — the new one never
  // appears, but its layer still swallows touches, so the app looks hung
  // (Catalog / Quick Replies opened their selector without even closing the
  // + menu). Every + menu option now runs only after the menu has fully
  // dismissed: iOS reports that via the Modal's onDismiss; other platforms
  // (no onDismiss) use a short timer. The ref guarantees a single run.
  const pendingAfterMenuCloseRef = useRef<(() => void) | null>(null);
  const runPendingAfterMenuClose = () => {
    const action = pendingAfterMenuCloseRef.current;
    pendingAfterMenuCloseRef.current = null;
    action?.();
  };
  const openAfterMenuCloses = (action: () => void) => {
    pendingAfterMenuCloseRef.current = action;
    setShowMediaOptions(false);
    // iOS: onDismiss normally fires first; this is only a safety net.
    setTimeout(runPendingAfterMenuClose, Platform.OS === 'ios' ? 700 : 300);
  };

  const handleMediaOption = async (type: string) => {
    console.log('🎯 [MessageInputWithVoice] handleMediaOption called with type:', type);
    setShowMediaOptions(false);
    setIsMediaLoading(true);
    
    try {
      switch (type) {
        case 'camera':
          console.log('📸 [MessageInputWithVoice] Camera option selected');
          // Hide loading before opening native camera
          setIsMediaLoading(false);
          if (!(await ensurePermission('camera', 'camera'))) return;
          const cameraResult = await MediaService.takePhoto();
          console.log('📸 [MessageInputWithVoice] Camera result:', cameraResult);
          if (cameraResult) {
            const caption = message.trim();
            console.log('📸 [MessageInputWithVoice] Sending image with caption:', { caption, length: caption.length });
            onSendMessage(caption, 'image', cameraResult.uri);
            setMessage('');
          }
          break;
        case 'gallery':
          console.log('🖼️ [MessageInputWithVoice] Gallery option selected');
          console.log('🖼️ [MessageInputWithVoice] About to call MediaService.pickImageFromGallery()');
          // Hide loading before opening native gallery
          setIsMediaLoading(false);
          if (!(await ensurePermission('mediaLibrary', 'photo library'))) return;
          const galleryResult = await MediaService.pickImageFromGallery();
          console.log('🖼️ [MessageInputWithVoice] MediaService.pickImageFromGallery() result:', galleryResult);
          if (galleryResult) {
            const caption = message.trim();
            console.log('🖼️ [MessageInputWithVoice] Sending image with caption:', { caption, length: caption.length });
            onSendMessage(caption, 'image', galleryResult.uri);
            setMessage('');
          }
          break;
        case 'video':
          // Hide loading before opening native video picker
          setIsMediaLoading(false);
          if (!(await ensurePermission('mediaLibrary', 'photo library'))) return;
          const videoResult = await MediaService.pickVideo();
          if (videoResult) {
            onSendMessage(message.trim(), 'video', videoResult.uri);
            setMessage('');
          }
          break;
        case 'audio':
          setIsMediaLoading(false);
          if (!(await ensurePermission('microphone', 'microphone'))) return;
          setShowVoiceRecorder(true);
          break;
        case 'document':
          console.log('📄 [MessageInputWithVoice] Document option selected');
          // Hide loading before opening document picker
          setIsMediaLoading(false);
          
          try {
            console.log('📄 [MessageInputWithVoice] About to call MediaService.pickDocument()');
            const documentResult = await MediaService.pickDocument();
            console.log('📄 [MessageInputWithVoice] Document picker result:', documentResult);
            
            if (documentResult) {
              console.log('📄 [MessageInputWithVoice] Document selected, checking file size...');
              
              // Check file size limit (10MB = 10 * 1024 * 1024 bytes)
              const maxSizeBytes = 10 * 1024 * 1024;
              if (documentResult.fileSize && documentResult.fileSize > maxSizeBytes) {
                Alert.alert(
                  'File Too Large',
                  'Please select a document smaller than 10MB.',
                  [{ text: 'OK' }]
                );
                return;
              }
              
              console.log('📄 [MessageInputWithVoice] Sending document message...');
              // Send document with proper metadata
              onSendMessage(
                message.trim(),
                'document',
                documentResult.uri,
                undefined, // audioDuration
                undefined, // locationData
                {
                  fileName: documentResult.fileName,
                  fileSize: documentResult.fileSize,
                  mimeType: documentResult.mimeType
                }
              );
              setMessage('');
            } else {
              console.log('📄 [MessageInputWithVoice] No document selected');
            }
          } catch (error) {
            console.error('❌ [MessageInputWithVoice] Error in document selection:', error);
            Alert.alert(
              'Document Selection Error',
              'Failed to select document. Please try again.',
              [{ text: 'OK' }]
            );
          }
          break;
        case 'location':
          setIsMediaLoading(false);
          if (!(await ensurePermission('location', 'location'))) return;
          setShowLocationPicker(true);
          break;
        case 'location_request':
          setIsMediaLoading(false);
          onSendMessage(
            message.trim(),
            'location_request'
          );
          setMessage('');
          break;
      }
    } catch (error) {
      console.error('❌ [MessageInputWithVoice] Error in media operation:', error);
    } finally {
      setIsMediaLoading(false);
    }
  };

  const handleAttachment = () => {
    setShowMediaOptions(true);
  };


  const handleTextChange = (text: string) => {
    // Update immediately without logging (logging causes lag)
    setMessage(text);
    // Call onChangeText if provided (for controlled components)
    onChangeText?.(text);
  };

  const handleEmojiSelect = (emoji: { emoji: string } | string) => {
    const emojiChar = typeof emoji === 'string' ? emoji : emoji.emoji;
    if (!emojiChar) return;
    const nextMessage = `${message}${emojiChar}`;
    setMessage(nextMessage);
    setShowEmojiKeyboard(false);
  };

  const filteredStickers = STICKERS.filter((sticker) => {
    if (!stickerSearchQuery.trim()) return true;
    const query = stickerSearchQuery.toLowerCase();
    return (
      sticker.name.toLowerCase().includes(query) ||
      sticker.tags.some((tag) => tag.toLowerCase().includes(query))
    );
  });

  const handleStickerSelect = async (sticker: (typeof STICKERS)[number]) => {
    try {
      setIsStickerLoading(true);
      setShowStickerPicker(false);

      const extension = sticker.url.split('.').pop() || 'gif';
        const baseDir = FileSystem.cacheDirectory || FileSystem.documentDirectory;
      if (!baseDir) {
        throw new Error('Sticker storage is unavailable on this device');
      }
      const fileUri = `${baseDir}sticker_${sticker.id}.${extension}`;

      const downloadResult = await FileSystem.downloadAsync(sticker.url, fileUri);
      if (!downloadResult?.uri) {
        throw new Error('Sticker download failed');
      }

      onSendMessage('', 'image', downloadResult.uri);
    } catch (error: any) {
      console.error('❌ [MessageInputWithVoice] Sticker send failed:', error);
      showNotification('error', 'Sticker Error', error?.message || 'Failed to send sticker');
    } finally {
      setIsStickerLoading(false);
    }
  };

  const isDisabled = disabled || sending;
  const canSend = (message.trim().length > 0 || !!selectedCatalog) && !isDisabled && !sendButtonDisabled;

  return (
    <View style={styles.container}>
      <View style={[styles.inputContainer, { flexDirection: rtl.rowDirection }]}>
        {/* Attachment Button */}
        {showAttachment && (
          <TouchableOpacity
            style={[styles.attachmentButton, isDisabled && styles.disabledButton, rtl.marginStart(spacing.sm)]}
            onPress={handleAttachment}
            disabled={isDisabled}
            activeOpacity={0.7}
          >
            <Ionicons 
              name="add" 
              size={24} 
              color={isDisabled ? colors.textLight : colors.primary} 
            />
          </TouchableOpacity>
        )}

        {/* Emoji Button (old placement) */}
        <TouchableOpacity
          style={[styles.emojiButton, isDisabled && styles.disabledButton, rtl.marginStart(spacing.sm)]}
          onPress={() => setShowEmojiKeyboard(true)}
          disabled={isDisabled}
          activeOpacity={0.7}
        >
          <Ionicons
            name="happy-outline"
            size={24}
            color={isDisabled ? colors.textLight : colors.primary}
          />
        </TouchableOpacity>

        {/* Text Input Container */}
        <View style={[styles.textInputContainer, rtl.marginStart(spacing.sm)]}>
          <RichTextEditor
            value={message}
            onChange={handleTextChange}
            placeholder={placeholder || t('chat.typeMessage')}
            maxLength={4096}
            showCharCount={false}
            editable={!isDisabled}
            variant="chat"
            showToolbar={false}
            onSubmitShortcut={handleSend}
          />
        </View>

        {/* Send Button */}
        {showSendButton && (
          <TouchableOpacity
            style={[
              styles.sendButton,
              canSend ? styles.sendButtonActive : styles.sendButtonInactive,
              isDisabled && styles.disabledButton
            ]}
            onPress={handleSend}
            disabled={!canSend}
            activeOpacity={0.8}
          >
            {sending ? (
              <Ionicons name="hourglass" size={20} color={colors.text.onPrimary} />
            ) : (
              <Ionicons
                name={rtl.mirrorIcon('send') as any}
                size={20}
                color={canSend ? colors.text.onPrimary : colors.textLight}
                style={isRTL ? { transform: [{ scaleX: -1 }] } : undefined}
              />
            )}
          </TouchableOpacity>
        )}
      </View>

      {selectedCatalog && (
        <View style={styles.attachmentPreviewContainer}>
          <View style={styles.catalogAttachmentChip}>
            <Ionicons name="grid-outline" size={16} color={colors.primary} />
            <Text style={styles.catalogAttachmentText} numberOfLines={1}>
              {selectedCatalog.name}
            </Text>
            <TouchableOpacity onPress={clearSelectedCatalog} style={styles.catalogAttachmentRemoveButton}>
              <Ionicons name="close-circle" size={18} color={colors.textLight} />
            </TouchableOpacity>
          </View>
        </View>
      )}

      {/* Media Loading Overlay */}
      {isMediaLoading && (
        <Modal
          visible={isMediaLoading}
          transparent={true}
          animationType="fade"
        >
          <View style={styles.loadingOverlay}>
            <View style={styles.loadingOverlayContainer}>
              <ActivityIndicator size="large" color="#007AFF" />
              <Text style={styles.loadingOverlayText}>Loading...</Text>
            </View>
          </View>
        </Modal>
      )}

      {/* Sticker Loading Overlay */}
      {isStickerLoading && (
        <Modal
          visible={isStickerLoading}
          transparent={true}
          animationType="fade"
        >
          <View style={styles.loadingOverlay}>
            <View style={styles.loadingOverlayContainer}>
              <ActivityIndicator size="large" color="#007AFF" />
              <Text style={styles.loadingOverlayText}>Sending sticker...</Text>
            </View>
          </View>
        </Modal>
      )}

      {/* Emoji Keyboard */}
      {isWeb ? (
        <Modal
          visible={showEmojiKeyboard}
          transparent={true}
          animationType="fade"
          onRequestClose={() => setShowEmojiKeyboard(false)}
        >
          <View style={styles.webEmojiBackdrop}>
            <View
              style={[
                styles.webEmojiPanel,
                modalHorizontalInset > 0 && {
                  left: modalHorizontalInset,
                  right: modalHorizontalInset,
                },
              ]}
            >
              <View style={styles.webEmojiHeader}>
                <Text style={styles.webEmojiTitle}>Emojis</Text>
                <TouchableOpacity
                  style={styles.webEmojiCloseButton}
                  onPress={() => setShowEmojiKeyboard(false)}
                >
                  <Ionicons name="close" size={18} color={colors.textDark} />
                </TouchableOpacity>
              </View>
              <EmojiKeyboard
                onEmojiSelected={handleEmojiSelect}
                enableSearchBar
                enableRecentlyUsed
                categoryPosition="top"
              />
            </View>
          </View>
        </Modal>
      ) : (
        <EmojiPicker
          open={showEmojiKeyboard}
          onClose={() => setShowEmojiKeyboard(false)}
          onEmojiSelected={handleEmojiSelect}
          enableSearchBar
          enableRecentlyUsed
          categoryPosition="top"
          theme={{
            backdrop: 'rgba(0, 0, 0, 0.35)'
          }}
        />
      )}

      {/* Sticker Picker Modal */}
      <Modal
        visible={showStickerPicker}
        transparent={true}
        animationType="slide"
        onRequestClose={() => {
          setShowStickerPicker(false);
          setStickerSearchQuery('');
        }}
      >
        <View style={styles.modalOverlay}>
          <View
            style={[
              styles.mediaOptionsContainer,
              modalHorizontalInset > 0 && {
                marginLeft: modalHorizontalInset,
                marginRight: modalHorizontalInset,
                width: modalContentWidth,
                alignSelf: 'center',
              },
            ]}
          >
            <View style={styles.mediaOptionsHeader}>
              <Text style={styles.mediaOptionsTitle}>Stickers</Text>
              <TouchableOpacity
                style={styles.closeButton}
                onPress={() => {
                  setShowStickerPicker(false);
                  setStickerSearchQuery('');
                }}
              >
                <Ionicons name="close" size={24} color={colors.textDark} />
              </TouchableOpacity>
            </View>

            <View style={styles.searchContainer}>
              <Ionicons name="search" size={20} color={colors.textLight} style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                placeholder={t('chat.searchStickers')}
                value={stickerSearchQuery}
                onChangeText={setStickerSearchQuery}
                placeholderTextColor={colors.textLight}
              />
              {stickerSearchQuery.length > 0 && (
                <TouchableOpacity
                  onPress={() => setStickerSearchQuery('')}
                  style={styles.clearSearchButton}
                >
                  <Ionicons name="close-circle" size={20} color={colors.textLight} />
                </TouchableOpacity>
              )}
            </View>

            <FlatList
              data={filteredStickers}
              keyExtractor={(item) => item.id}
              numColumns={4}
              contentContainerStyle={styles.stickerGrid}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={styles.stickerItem}
                  onPress={() => handleStickerSelect(item)}
                  activeOpacity={0.8}
                >
                  <Image source={{ uri: item.url }} style={styles.stickerImage} />
                </TouchableOpacity>
              )}
              ListEmptyComponent={
                <View style={styles.emptyContainer}>
                  <Ionicons name="sparkles" size={48} color={colors.textLight} />
                  <Text style={styles.emptyText}>No stickers found</Text>
                </View>
              }
            />
          </View>
        </View>
      </Modal>

      {/* Media Options Modal */}
      <Modal
        visible={showMediaOptions}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowMediaOptions(false)}
        onDismiss={runPendingAfterMenuClose}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowMediaOptions(false)}
        >
          <View
            style={[
              styles.mediaOptionsContainer,
              modalHorizontalInset > 0 && {
                marginLeft: modalHorizontalInset,
                marginRight: modalHorizontalInset,
                width: modalContentWidth,
                alignSelf: 'center',
              },
            ]}
          >
            <View style={styles.mediaOptionsHeader}>
              <Text style={styles.mediaOptionsTitle}>Send Media</Text>
              <TouchableOpacity
                style={styles.closeButton}
                onPress={() => setShowMediaOptions(false)}
              >
                <Ionicons name="close" size={24} color={colors.textDark} />
              </TouchableOpacity>
            </View>
            
            <View
              style={[
                styles.mediaOptionsGrid,
                modalHorizontalInset > 0 && styles.mediaOptionsGridDesktop,
              ]}
            >
              {mediaOptions.map((option) => (
                <TouchableOpacity
                  key={option.id}
                  style={[styles.mediaOptionItem, { width: mediaOptionWidth }]}
                  onPress={() => openAfterMenuCloses(option.onPress)}
                  activeOpacity={0.7}
                >
                  <View style={[styles.mediaOptionIcon, { backgroundColor: option.color }]}>
                    <Ionicons name={option.icon as any} size={24} color="white" />
                  </View>
                  <Text style={styles.mediaOptionText}>{option.title}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </View>
        </TouchableOpacity>
      </Modal>

      {/* Voice Recorder Modal */}
      <Modal
        visible={showVoiceRecorder}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setShowVoiceRecorder(false)}
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowVoiceRecorder(false)}
        >
          <VoiceRecorder
            onRecordingComplete={handleVoiceRecordingComplete}
            onRecordingCancel={handleVoiceRecordingCancel}
            maxDuration={120}
          />
        </TouchableOpacity>
      </Modal>

      {/* Map Location Picker Modal */}
      <MapLocationPicker
        visible={showLocationPicker}
        onLocationSelect={handleLocationSelect}
        onClose={handleLocationPickerClose}
      />

      {/* Quick Reply Set Selector Modal */}
      <Modal
        visible={showQuickReplySelector}
        transparent={true}
        animationType="slide"
        onRequestClose={() => {
          setShowQuickReplySelector(false);
          setQuickReplySearchQuery('');
        }}
      >
        <View style={styles.modalOverlay}>
          <View
            style={[
              styles.mediaOptionsContainer,
              modalHorizontalInset > 0 && {
                marginLeft: modalHorizontalInset,
                marginRight: modalHorizontalInset,
                width: modalContentWidth,
                alignSelf: 'center',
              },
            ]}
          >
            <View style={styles.mediaOptionsHeader}>
              <Text style={styles.mediaOptionsTitle}>Select Quick Reply Set</Text>
              <TouchableOpacity
                style={styles.closeButton}
                onPress={() => {
                  setShowQuickReplySelector(false);
                  setQuickReplySearchQuery('');
                }}
              >
                <Ionicons name="close" size={24} color={colors.textDark} />
              </TouchableOpacity>
            </View>

            {/* Search Bar */}
            <View style={styles.searchContainer}>
              <Ionicons name="search" size={20} color={colors.textLight} style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                placeholder={t('chat.searchQuickReplies')}
                value={quickReplySearchQuery}
                onChangeText={setQuickReplySearchQuery}
                placeholderTextColor={colors.textLight}
              />
              {quickReplySearchQuery.length > 0 && (
                <TouchableOpacity
                  onPress={() => setQuickReplySearchQuery('')}
                  style={styles.clearSearchButton}
                >
                  <Ionicons name="close-circle" size={20} color={colors.textLight} />
                </TouchableOpacity>
              )}
            </View>

            {/* Quick Reply Sets List */}
            {loadingQuickReplySets ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colors.primary} />
                <Text style={styles.loadingText}>Loading sets...</Text>
              </View>
            ) : (
              <FlatList
                data={filteredQuickReplySets}
                keyExtractor={(item) => item._id}
                style={styles.quickReplySetsList}
                renderItem={({ item: set }) => (
                  <TouchableOpacity
                    style={styles.quickReplySetItem}
                    onPress={() => handleSendQuickReplySet(set._id)}
                    disabled={sendingQuickReplySet}
                    activeOpacity={0.7}
                  >
                    <View style={styles.quickReplySetItemContent}>
                      <Ionicons name="chatbubble-ellipses-outline" size={24} color={colors.primary} />
                      <View style={styles.quickReplySetItemText}>
                        <Text style={styles.quickReplySetItemName}>{set.name}</Text>
                        {set.description && (
                          <Text style={styles.quickReplySetItemDescription} numberOfLines={1}>
                            {set.description}
                          </Text>
                        )}
                        {set.itemCount !== undefined && (
                          <Text style={styles.quickReplySetItemCount}>
                            {set.itemCount} {set.itemCount === 1 ? 'item' : 'items'}
                          </Text>
                        )}
                      </View>
                    </View>
                    {sendingQuickReplySet ? (
                      <ActivityIndicator size="small" color={colors.primary} />
                    ) : (
                      <DirectionalIcon name="chevron-forward" size={20} color={colors.textLight} />
                    )}
                  </TouchableOpacity>
                )}
                ListEmptyComponent={
                  <View style={styles.emptyContainer}>
                    <Ionicons name="chatbubble-ellipses-outline" size={48} color={colors.textLight} />
                    <Text style={styles.emptyText}>
                      {quickReplySets.length === 0
                        ? 'No quick reply sets available'
                        : 'No sets found'}
                    </Text>
                  </View>
                }
              />
            )}
          </View>
        </View>
      </Modal>

      {/* Catalog Selector Modal */}
      <Modal
        visible={showCatalogSelector}
        transparent={true}
        animationType="slide"
        onRequestClose={() => {
          setShowCatalogSelector(false);
          setCatalogSearchQuery('');
        }}
      >
        <View style={styles.modalOverlay}>
          <View
            style={[
              styles.mediaOptionsContainer,
              modalHorizontalInset > 0 && {
                marginLeft: modalHorizontalInset,
                marginRight: modalHorizontalInset,
                width: modalContentWidth,
                alignSelf: 'center',
              },
            ]}
          >
            <View style={styles.mediaOptionsHeader}>
              <Text style={styles.mediaOptionsTitle}>Select Catalog</Text>
              <TouchableOpacity
                style={styles.closeButton}
                onPress={() => {
                  setShowCatalogSelector(false);
                  setCatalogSearchQuery('');
                }}
              >
                <Ionicons name="close" size={24} color={colors.textDark} />
              </TouchableOpacity>
            </View>

            {/* Search Bar */}
            <View style={styles.searchContainer}>
              <Ionicons name="search" size={20} color={colors.textLight} style={styles.searchIcon} />
              <TextInput
                style={styles.searchInput}
                placeholder={t('chat.searchCatalogs')}
                value={catalogSearchQuery}
                onChangeText={setCatalogSearchQuery}
                placeholderTextColor={colors.textLight}
              />
              {catalogSearchQuery.length > 0 && (
                <TouchableOpacity
                  onPress={() => setCatalogSearchQuery('')}
                  style={styles.clearSearchButton}
                >
                  <Ionicons name="close-circle" size={20} color={colors.textLight} />
                </TouchableOpacity>
              )}
            </View>

            {/* Catalogues List */}
            {loadingCatalogues ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color={colors.primary} />
                <Text style={styles.loadingText}>Loading catalogs...</Text>
              </View>
            ) : (
              <FlatList
                data={filteredCatalogues}
                keyExtractor={(item) => item._id}
                style={styles.quickReplySetsList}
                renderItem={({ item: catalog }) => (
                  <TouchableOpacity
                    style={styles.quickReplySetItem}
                    onPress={() => handleSelectCatalog(catalog)}
                    activeOpacity={0.7}
                  >
                    <View style={styles.quickReplySetItemContent}>
                      <Ionicons name="grid-outline" size={24} color={colors.primary} />
                      <View style={styles.quickReplySetItemText}>
                        <Text style={styles.quickReplySetItemName}>{catalog.name}</Text>
                        {catalog.description && (
                          <Text style={styles.quickReplySetItemDescription} numberOfLines={1}>
                            {catalog.description}
                          </Text>
                        )}
                        {catalog.productCount !== undefined && (
                          <Text style={styles.quickReplySetItemCount}>
                            {catalog.productCount} {catalog.productCount === 1 ? 'product' : 'products'}
                          </Text>
                        )}
                      </View>
                    </View>
                    <DirectionalIcon name="chevron-forward" size={20} color={colors.textLight} />
                  </TouchableOpacity>
                )}
                ListEmptyComponent={
                  <View style={styles.emptyContainer}>
                    <Ionicons name="grid-outline" size={48} color={colors.textLight} />
                    <Text style={styles.emptyText}>
                      {catalogues.length === 0
                        ? 'No catalogs available'
                        : 'No catalogs found'}
                    </Text>
                  </View>
                }
              />
            )}
          </View>
        </View>
      </Modal>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  container: {
    backgroundColor: 'transparent',
    paddingBottom: Platform.OS === 'ios' ? 0 : spacing.sm,
    position: 'relative',
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    paddingHorizontal: spacing.sm,
    paddingVertical: spacing.sm,
    minHeight: 60,
    backgroundColor: 'transparent',
  },
  attachmentButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 2,
  },
  emojiButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: 'transparent',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 2,
  },
  attachmentIcon: {
    fontSize: 18,
    color: colors.text.primary,
  },
  textInputContainer: {
    flex: 1,
    position: 'relative',
    marginRight: spacing.sm,
  },
  attachmentPreviewContainer: {
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.xs,
  },
  catalogAttachmentChip: {
    alignSelf: 'flex-start',
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: withAlpha(colors.primary, 0.25),
    backgroundColor: withAlpha(colors.primary, 0.07),
    borderRadius: 16,
    paddingVertical: 6,
    paddingHorizontal: 10,
    maxWidth: '95%',
  },
  catalogAttachmentText: {
    marginLeft: 6,
    marginRight: 4,
    color: colors.textDark,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
    maxWidth: 220,
  },
  catalogAttachmentRemoveButton: {
    marginLeft: 2,
  },
  sendButton: {
    width: 46,
    height: 46,
    borderRadius: 23,
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 0,
  },
  sendButtonActive: {
    backgroundColor: colors.primary,
  },
  sendButtonInactive: {
    backgroundColor: chat.in.chip,
  },
  sendIcon: {
    fontSize: 18,
    fontWeight: '600',
  },
  sendIconActive: {
    color: colors.white,
  },
  sendIconInactive: {
    color: colors.text.secondary,
  },
  disabledButton: {
    opacity: 0.5,
  },
  disabledText: {
    color: colors.textLight,
  },
  webEmojiBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
  },
  webEmojiPanel: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 64,
    bottom: 0,
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.border,
    ...designTokens.shadows.lg,
  },
  webEmojiHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: designTokens.spacing.md,
    paddingVertical: designTokens.spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: withAlpha(colors.border, 0.13),
  },
  webEmojiTitle: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },
  webEmojiCloseButton: {
    width: 28,
    height: 28,
    borderRadius: 14,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: withAlpha(colors.lightGray, 0.25),
  },

  // Media Options Modal Styles
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },

  mediaOptionsContainer: {
    backgroundColor: colors.background.surface,
    borderTopLeftRadius: designTokens.borderRadius.xl,
    borderTopRightRadius: designTokens.borderRadius.xl,
    paddingBottom: Platform.OS === 'ios' ? 34 : designTokens.spacing.lg,
    ...designTokens.shadows.xl,
  },

  mediaOptionsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: designTokens.spacing.lg,
    paddingVertical: designTokens.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: withAlpha(colors.border, 0.13),
  },

  mediaOptionsTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
  },

  closeButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: withAlpha(colors.lightGray, 0.25),
  },

  mediaOptionsGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    paddingHorizontal: designTokens.spacing.lg,
    paddingTop: designTokens.spacing.lg,
    justifyContent: 'space-between',
  },
  mediaOptionsGridDesktop: {
    justifyContent: 'flex-start',
    columnGap: designTokens.spacing.md,
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: designTokens.spacing.lg,
    paddingVertical: designTokens.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: withAlpha(colors.border, 0.13),
  },
  searchIcon: {
    marginRight: designTokens.spacing.sm,
  },
  searchInput: {
    flex: 1,
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.primary,
    paddingVertical: designTokens.spacing.xs,
  },
  clearSearchButton: {
    marginLeft: designTokens.spacing.sm,
    padding: designTokens.spacing.xs / 2,
  },
  loadingContainer: {
    padding: designTokens.spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingText: {
    marginTop: designTokens.spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  quickReplySetsList: {
    maxHeight: 400,
    paddingVertical: designTokens.spacing.sm,
  },
  quickReplySetItem: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: designTokens.spacing.lg,
    paddingVertical: designTokens.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: withAlpha(colors.border, 0.06),
  },
  quickReplySetItemContent: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
  },
  quickReplySetItemText: {
    marginLeft: designTokens.spacing.md,
    flex: 1,
  },
  quickReplySetItemName: {
    fontSize: typography.fontSize.base,
    fontWeight: '600',
    color: colors.textDark,
    fontFamily: typography.fontFamily.semibold,
    marginBottom: designTokens.spacing.xs / 2,
  },
  quickReplySetItemDescription: {
    fontSize: typography.fontSize.sm,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    marginBottom: designTokens.spacing.xs / 2,
  },
  quickReplySetItemCount: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
  },
  emptyContainer: {
    padding: designTokens.spacing.xl,
    alignItems: 'center',
    justifyContent: 'center',
  },
  emptyText: {
    marginTop: designTokens.spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textLight,
    fontFamily: typography.fontFamily.primary,
    textAlign: 'center',
  },
  stickerGrid: {
    padding: designTokens.spacing.lg,
  },
  stickerItem: {
    width: '25%',
    alignItems: 'center',
    paddingVertical: designTokens.spacing.sm,
  },
  stickerImage: {
    width: 64,
    height: 64,
    borderRadius: 12,
    backgroundColor: colors.lightGray,
  },

  mediaOptionItem: {
    width: (screenWidth - designTokens.spacing.lg * 2 - designTokens.spacing.md * 2) / 3,
    alignItems: 'center',
    marginBottom: designTokens.spacing.lg,
  },

  mediaOptionIcon: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: designTokens.spacing.sm,
    ...designTokens.shadows.md,
  },

  mediaOptionText: {
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
    textAlign: 'center',
  },

  loadingOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'center',
    alignItems: 'center',
  },

  loadingOverlayContainer: {
    backgroundColor: colors.background.primary,
    padding: designTokens.spacing.xl,
    borderRadius: designTokens.borderRadius.lg,
    alignItems: 'center',
    ...designTokens.shadows.lg,
  },

  loadingOverlayText: {
    marginTop: designTokens.spacing.md,
    fontSize: typography.fontSize.base,
    color: colors.textDark,
    fontFamily: typography.fontFamily.medium,
  },
});
